import React, { useState } from 'react'
import { useLocation, useNavigate } from "react-router-dom";
import { FiMenu } from "react-icons/fi";
import { MdClose } from "react-icons/md";

const   Navbar = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [activeLink, setActiveLink] = useState("home");
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { id: "home", label: "Home", path: "/" },
    { id: "projects", label: "Projects", path: "/projects" },
    { id: "features", label: "Services", path: "/services" },
    { id: "resume", label: "Resume", path: "/about" },
    { id: "contact", label: "Contact", path: "/contact" },
  ];

  const handleNavClick = (item) => {
    setActiveLink(item.id);
    setShowMenu(false);
    navigate(item.path);
  };

  return (
    <div className="fixed inset-x-0 top-6 z-50 w-full transition-all duration-300 ease-out px-4">
      {/* Desktop Nav */}
      <nav className="relative z-[70] mx-auto hidden lg:flex items-center justify-between rounded-full border border-white/10 px-6 py-3 transition-all duration-300 ease-out bg-black/40 backdrop-blur-md shadow-[0_0_12px_rgba(6,182,212,0.05)] w-[90%] max-w-6xl">
        {/* Logo */}
        <div className="flex-shrink-0">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="relative z-20 flex items-center space-x-2 px-2 py-1 group">
            <div className="relative flex items-center space-x-2">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-md rounded-lg group-hover:blur-lg transition-all duration-300"></div>
              <div className="relative z-10 flex items-center space-x-2">
                <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full animate-pulse"></div>
                <div className="flex items-baseline space-x-0">
                  <span className="font-serifFont font-semibold text-white tracking-wide text-lg group-hover:text-cyan-300 transition-colors duration-300 italic">R</span>
                  <span className="text-xs font-monoFont text-cyan-400 opacity-80 group-hover:opacity-100 transition-opacity duration-300">.dev()</span>
                </div>
              </div>
            </div>
          </a>
        </div>

        {/* Nav Links */}
        <div className="flex items-center space-x-2">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={item.path}
              onClick={(e) => { e.preventDefault(); handleNavClick(item); }}
              className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ease-in-out cursor-pointer ${
                activeLink === item.id || location.pathname === item.path
                  ? "text-cyan-400 border border-cyan-400/60 bg-cyan-400/5 shadow-md shadow-cyan-500/20"
                  : "text-white hover:text-cyan-300 border border-transparent hover:border-cyan-400/30"
              }`}
            >
              <span className="relative z-20">{item.label}</span>
            </a>
          ))}
        </div>

        {/* Contact Button */}
        <div className="flex-shrink-0">
          <a
            href="/contact"
            onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
            className="px-6 py-2.5 rounded-full text-sm font-medium relative cursor-pointer transition-all duration-300 inline-block text-center group overflow-hidden bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-md border border-cyan-500/30 text-white hover:from-cyan-500/30 hover:to-blue-500/30 hover:border-cyan-400/50 shadow-lg shadow-cyan-500/20 hover:shadow-xl hover:shadow-cyan-500/30 hover:scale-105 hover:-translate-y-0.5"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-blue-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <span className="relative z-10 font-semibold tracking-wide">Contact</span>
          </a>
        </div>
      </nav>

      {/* Mobile Nav */}
      <nav className="relative z-[60] mx-auto flex lg:hidden w-full max-w-[calc(100vw-2rem)] flex-col border border-white/10 px-4 py-3 rounded-2xl transition-all duration-300 ease-out bg-black/40 backdrop-blur-md shadow-md">
        <div className="flex w-full items-center justify-between">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="relative z-20 flex items-center space-x-2 px-2 py-1 group">
            <div className="relative flex items-center space-x-2">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-md rounded-lg group-hover:blur-lg transition-all duration-300"></div>
              <div className="relative z-10 flex items-center space-x-2">
                <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full animate-pulse"></div>
                <div className="flex items-baseline space-x-0">
                  <span className="font-serifFont font-semibold text-white tracking-wide text-lg group-hover:text-cyan-300 transition-colors duration-300 italic">R</span>
                  <span className="text-xs font-monoFont text-cyan-400 opacity-80 group-hover:opacity-100 transition-opacity duration-300">.dev()</span>
                </div>
              </div>
            </div>
          </a>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-3 min-h-[48px] min-w-[48px] inline-flex items-center justify-center text-white hover:text-cyan-300 transition-colors"
            aria-label="Open menu"
          >
            {showMenu ? <MdClose className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`mt-4 flex flex-col space-y-2 border-t border-cyan-500/20 overflow-hidden transition-all duration-300 ease-in-out pt-0 ${
          showMenu ? "max-h-96 opacity-100 pt-4" : "max-h-0 opacity-0"
        }`}>
          {navItems.map((item) => (
            <a
              key={item.id}
              href={item.path}
              onClick={(e) => { e.preventDefault(); handleNavClick(item); }}
              className={`relative group px-3 py-3 min-h-[48px] flex items-center rounded-lg transition-all duration-200 ease-in-out w-full text-left cursor-pointer ${
                activeLink === item.id || location.pathname === item.path
                  ? "text-cyan-400 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-2 border-cyan-400/70 shadow-lg shadow-cyan-500/20"
                  : "text-white hover:text-cyan-300 hover:bg-white/5 border-2 border-transparent hover:border-cyan-400/30"
              }`}
            >
              <span className="block text-sm font-medium relative z-10">{item.label}</span>
            </a>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default Navbar;
